import type { SVGProps } from 'react';

import type { IconName } from '@/shared/icons/icon-paths';
import { iconPaths } from '@/shared/icons/icon-paths';

export type { IconName };

export type IconProps = Omit<SVGProps<SVGSVGElement>, 'name' | 'stroke' | 'strokeWidth'> & {
  name: IconName;
  /** 24 dans la navigation, 18 dans un bouton, 20 dans une ligne de liste. */
  size?: number;
  /** La charte impose 1,75 partout ; on ne l'épaissit que pour une coche. */
  strokeWidth?: number;
  /** Nom lu par un lecteur d'écran. Absent : l'icône est décorative. */
  label?: string;
};

/**
 * Icône du jeu BUL : grille de 24, trait de 1,75, extrémités arrondies. Les
 * tracés sont ceux du mobile et de la référence HTML (`bul/design/`) : le web,
 * le mobile et la charte ne divergent jamais.
 *
 * Toujours monochrome, elle hérite de la couleur du texte (`currentColor`) : une
 * icône ne porte jamais d'information par la couleur, sauf la marque de
 * certification et les réactions, qui ont leur jeton.
 */
export function Icon({
  name,
  size = 24,
  strokeWidth = 1.75,
  label,
  className,
  ...rest
}: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      focusable="false"
      className={className}
      style={{ flexShrink: 0 }}
      {...rest}
    >
      {iconPaths[name].map((path, index) => (
        <path
          key={`${name}-${index}`}
          d={path.d}
          fill={path.fill ? (path.color ?? 'currentColor') : 'none'}
          fillRule={path.fillRule}
          stroke={path.fill ? 'none' : (path.color ?? 'currentColor')}
          strokeWidth={path.strokeWidth ?? strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
    </svg>
  );
}
